declare global {
  interface Window {
    // Global command queue from the Ketch smart tag, loaded app-wide in the document head.
    ketch?: (method: string, options?: { tab?: string }) => void
  }
}

// The marketing site base URL, published by Rails as a <meta> tag from Webflow::PUBLIC_URL so it
// stays a single source of truth rather than a duplicated constant.
function marketingUrl(): string {
  return document.querySelector('meta[name="marketing-url"]')?.getAttribute("content") ?? ""
}

// Opens the Ketch consent preferences; `tab` selects a panel (omitted opens the default view).
function openKetchPreferences(tab?: string) {
  if (typeof window.ketch !== "function") return
  window.ketch("showPreferences", tab ? { tab } : undefined)
}

// Makes an anchor that opens a popup rather than a page behave as a button. It has no href, so
// store scripts that rewrite every `footer a[href]` to open in a new tab leave it alone.
function actAsButton(anchor: HTMLAnchorElement, onActivate: () => void) {
  anchor.setAttribute("role", "button")
  anchor.tabIndex = 0
  anchor.style.cursor = "pointer"
  anchor.addEventListener("click", (event) => {
    event.preventDefault()
    onActivate()
  })
  anchor.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return
    event.preventDefault()
    onActivate()
  })
}

/**
 * A single legal/privacy link. Renders a bare <a> into its own light DOM so the surrounding
 * footer's styles apply, and stays layout-transparent (display: contents) so it can be dropped
 * straight into a store's own footer markup (e.g. inside an <li>) and look native.
 */
abstract class PrivacyLink extends HTMLElement {
  connectedCallback() {
    if (this.firstElementChild) return

    this.style.display = "contents"
    const anchor = document.createElement("a")
    anchor.textContent = this.label
    this.configure(anchor)
    this.append(anchor)
  }

  protected abstract get label(): string
  protected abstract configure(anchor: HTMLAnchorElement): void
}

class PrivacyTermsLink extends PrivacyLink {
  protected get label() {
    return "Terms"
  }

  protected configure(anchor: HTMLAnchorElement) {
    anchor.href = `${marketingUrl()}/legal/terms`
    anchor.target = "_blank"
    anchor.rel = "noopener"
  }
}

class PrivacyPolicyLink extends PrivacyLink {
  protected get label() {
    return "Privacy Policy"
  }

  protected configure(anchor: HTMLAnchorElement) {
    anchor.href = `${marketingUrl()}/legal/privacy`
    anchor.target = "_blank"
    anchor.rel = "noopener"
  }
}

class PrivacyCookiesLink extends PrivacyLink {
  protected get label() {
    return "Cookies & Trackers"
  }

  protected configure(anchor: HTMLAnchorElement) {
    actAsButton(anchor, () => openKetchPreferences("consentsTab"))
  }
}

class PrivacyChoicesLink extends PrivacyLink {
  protected get label() {
    return "Your Privacy Choices"
  }

  protected configure(anchor: HTMLAnchorElement) {
    actAsButton(anchor, () => openKetchPreferences())
  }
}

const LINK_TAGS = ["privacy-terms-link", "privacy-policy-link", "privacy-cookies-link", "privacy-choices-link"]

// Layout for the container's link row. Colors and spacing read from --privacy-footer-* custom
// properties (light DOM, so a surface sets them with ordinary CSS) and default to the dark
// storefront footer. Only anchors inside a <privacy-footer> match, so the individual link
// elements placed bare in a custom footer are left to inherit that store's styling.
const CONTAINER_STYLES = `
  privacy-footer {
    display: flex;
    flex-wrap: wrap;
    justify-content: var(--privacy-footer-justify, center);
    align-items: baseline;
    column-gap: var(--privacy-footer-column-gap, 20px);
    row-gap: var(--privacy-footer-row-gap, 6px);
  }
  privacy-footer a {
    color: var(--privacy-footer-color, #9c9c9c);
    font-size: 14px;
    font-weight: 400;
    text-decoration: none;
    cursor: pointer;
  }
  privacy-footer a:hover {
    color: var(--privacy-footer-hover-color, #fff);
  }
`

/**
 * Lays the four privacy links out as a row on Brilliant-controlled surfaces (the default store
 * footer, dashboard, marketing, and bare footers). Custom store footers place the individual
 * link elements themselves so they inherit the store's styling, and do not use this container.
 */
class PrivacyFooter extends HTMLElement {
  connectedCallback() {
    // Runs on every (re)connect, not just the first: the dashboard's <shadow-realm> moves this
    // element into its shadow root after the initial light-DOM connect, and the styles have to be
    // injected into whichever root the element currently lives in.
    this.injectStyles()

    this.setAttribute("role", "navigation")
    this.setAttribute("aria-label", "Legal and privacy")
    // Fills in whichever of the four links the author left out, in their canonical order.
    this.append(...LINK_TAGS.map((tag) => this.querySelector(tag) ?? document.createElement(tag)))
  }

  // Styles are scoped to the root the container lives in, so they also reach a container rendered
  // inside a shadow root (e.g. the dashboard's <shadow-realm>), which document-level styles can't.
  private injectStyles() {
    const root = this.getRootNode()
    const target = root instanceof ShadowRoot ? root : document.head
    if (target.querySelector("style[data-privacy-footer]")) return

    const style = document.createElement("style")
    style.setAttribute("data-privacy-footer", "")
    style.textContent = CONTAINER_STYLES
    target.append(style)
  }
}

const REGISTRY: Record<string, CustomElementConstructor> = {
  "privacy-terms-link": PrivacyTermsLink,
  "privacy-policy-link": PrivacyPolicyLink,
  "privacy-cookies-link": PrivacyCookiesLink,
  "privacy-choices-link": PrivacyChoicesLink,
  "privacy-footer": PrivacyFooter,
}

export function definePrivacyFooterElements() {
  for (const [tag, constructor] of Object.entries(REGISTRY)) {
    if (!customElements.get(tag)) customElements.define(tag, constructor)
  }
}

export { PrivacyFooter, PrivacyTermsLink, PrivacyPolicyLink, PrivacyCookiesLink, PrivacyChoicesLink }
